'use client'

import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label} from '@headlessui/react'
import {CheckIcon, ChevronUpDownIcon} from '@heroicons/react/20/solid'
import {useState} from 'react'
import {XMarkIcon} from "@heroicons/react/24/outline";
import {cn} from "../../lib/utils.ts";
import {fieldVariants} from "./fieldStyles.ts";

type Placement = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'end';
type AnchorProps = false | 
    `${Placement}` | 
    `${Placement} ${Align}` | 
    Partial<{
        gap: number | string;
        offset: number | string;
        padding: number | string;
        to: `${Placement}` | `${Placement} ${Align}`;
    }>;

interface ComboboxOption {
    displayName: string,
    id: string,
    searchAliases?: string[],
}

type MapperFn<FromT, ToT> = (from: FromT) => ToT;

function transform_all<FromT, ToT>(from: FromT[], mapping: MapperFn<FromT, ToT>): ToT[] {
    return from.map(mapping)
}

interface Props<OptionT> {
    label?: string,
    className?: string,
    options: OptionT[]
    onSelect: (selected: ComboboxOption & OptionT | null) => void,
    selected: OptionT,
    emptySelectionMessage: string,
    disabled?: boolean,
    internalMapper: MapperFn<OptionT, ComboboxOption & OptionT>,
    onCreateClick?: (query: string) => void,
    showClearButton?: boolean,
    anchor?: AnchorProps,
    menuWidth?: string,
    menuClassName?: string,
    comboBoxClassName?: string,
    openMenuOnFocus?: boolean,
    ariaLabel?: string,
    ariaLabelledBy?: string,
}

function getAvailableOptions<OptionT>(options: (ComboboxOption & OptionT)[], query: string): OptionT[] {
    return query === ''
        ? options
        : options.filter((option) => {
            const needle = query.toLowerCase()
            return [option.displayName, ...(option.searchAliases ?? [])]
                .some((haystack) => haystack.toLowerCase().includes(needle))
        })
}

export function ComboboxList<OptionT>({
                                          label,
                                          className,
                                          options,
                                          onSelect,
                                          selected,
                                          emptySelectionMessage,
                                          internalMapper,
                                          disabled,
                                          onCreateClick,
                                          showClearButton = true,
                                          anchor = false,
                                          menuWidth = 'w-full',
                                          menuClassName,
                                          comboBoxClassName,
                                          openMenuOnFocus = false,
                                          ariaLabel,
                                          ariaLabelledBy,
                                      }: Props<OptionT>) {

    const comboboxOptions = transform_all<OptionT, ComboboxOption & OptionT>(options, internalMapper)
    const [query, setQuery] = useState<string>('')

    const filteredOptions = getAvailableOptions<ComboboxOption & OptionT>(comboboxOptions, query)

    let selectedComboboxOption;

    if (selected !== null && selected !== undefined)
        selectedComboboxOption = internalMapper(selected)
    else
        selectedComboboxOption = null


    return (
        <div className={comboBoxClassName ?? 'max-w-96'}>
            <Combobox
                as="div"
                disabled={disabled}
                value={selectedComboboxOption}
                by="id"
                immediate={openMenuOnFocus}
                onChange={(option: ComboboxOption & OptionT) => {
                    setQuery('')
                    onSelect(option)
                }}
            >
               {label && <Label className="block text-sm/6 font-medium text-gray-900">{label}</Label>}
                <div className={`relative ${label ? 'mt-2' : ''}`}>
                    <ComboboxInput
                        aria-label={ariaLabel}
                        aria-labelledby={ariaLabelledBy}
                        placeholder={emptySelectionMessage}
                        className={cn(fieldVariants(), "pr-12", className)}
                        onChange={(event) => setQuery(event.target.value)}
                        onBlur={() => setQuery('')}
                        value={query !== '' ? query : (selectedComboboxOption?.displayName || '')}
                        displayValue={(value: OptionT & ComboboxOption) => (
                            value?.displayName
                        )}
                    />
                    <ComboboxButton
                        className="disabled:hidden absolute inset-y-0 right-0 flex items-center gap-1 rounded-r-md px-2 focus:outline-hidden">
                        {onCreateClick && !disabled && (
                            <button
                                type="button"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onCreateClick(query);
                                }}
                                className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-300 text-xs font-bold text-white"
                                aria-label="Nieuwe relatie"
                            >
                                +
                            </button>
                        )}
                        <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true"/>
                    </ComboboxButton>
                    {!disabled && showClearButton && (
                        <button
                            type="button"
                            onClick={() => onSelect(null)}
                            className="absolute inset-y-0 right-6 flex items-center rounded-r-md px-2 focus:outline-hidden"
                            aria-label="Selectie wissen">
                            <XMarkIcon className="size-5 text-gray-400 bg-white" aria-hidden="true"/>
                        </button>
                    )}

                    {filteredOptions.length > 0 && (
                        <ComboboxOptions
                            anchor={anchor}
                            className={`absolute z-50 mt-1 max-h-60 ${menuWidth} overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm`}>
                            {filteredOptions.map((option) => (
                                <ComboboxOption
                                    key={option.id}
                                    value={option}
                                    className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-[focus]:bg-primary data-[focus]:text-white data-[focus]:outline-hidden"
                                >
                                <span
                                    className="block truncate group-data-[selected]:font-semibold">{option.displayName}</span>

                                    <span
                                        className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary group-data-[focus]:text-white group-data-[selected]:flex">
                  <CheckIcon className="size-5" aria-hidden="true"/>
                </span>
                                </ComboboxOption>
                            ))}

                        </ComboboxOptions>
                    )}
                    {!filteredOptions.length && (
                        <ComboboxOptions
                            anchor={anchor}
                            className={`absolute z-50 mt-1 max-h-60 ${menuWidth} overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm`}>

                            <ComboboxOption
                                key={1}
                                disabled={true}
                                className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-[focus]:bg-primary data-[focus]:text-white data-[focus]:outline-hidden"
                                value={null}>
                                <span
                                    className="block truncate group-data-[selected]:font-semibold text-gray-500">Geen data beschikbaar</span>

                                <span
                                    className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary group-data-[focus]:text-white group-data-[selected]:flex">
                </span>
                            </ComboboxOption>


                        </ComboboxOptions>
                    )}
                </div>
            </Combobox>
        </div>

    )
}
